import {  t  } from "@/client/libs/dummy-i18n";
import { 
  CheckCircleIcon,
  CrownIcon, 
  DiamondIcon,
  LightningIcon, 
  SparkleIcon 
} from "@phosphor-icons/react";
import { Button } from "@stackmorph/ui";
import { cn } from "@stackmorph/utils";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

import { useUser } from "@/client/services/user";

import { AddonsMarketplace } from "./_sections/addons-marketplace";
import { FeatureComparison } from "./_sections/feature-comparison";
import { ReferralDashboard } from "./_sections/referral-dashboard";
import { UsageAnalytics } from "./_sections/usage-analytics";

export const PremiumPage = () => {
  const { user } = useUser();

  return (
    <>
      <Helmet>
        <title>
          {t`Premium`} - {t`StackMorph Resume Builder`}
        </title>
      </Helmet>

      <div className="space-y-8 p-6 lg:p-10">
        {/* Membership Status Section */}
        <div className="grid gap-8 lg:grid-cols-2">
          {/* Left: The Membership Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative aspect-[1.586/1] overflow-hidden rounded-3xl bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-950 p-8 text-white shadow-2xl shadow-black/50 ring-1 ring-white/10"
          >
            {/* Card Texture/Shine */}
            <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-20" />
            <div className="absolute -right-20 -top-20 size-64 rounded-full bg-primary/30 blur-3xl" />
            
            <div className="relative z-10 flex h-full flex-col justify-between">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-white/10 backdrop-blur-md">
                    <CrownIcon 
                      size={24} 
                      weight="fill" 
                      className={user?.isPremium ? "text-yellow-400" : "text-white/50"} 
                    />
                  </div>
                  <div>
                    <h3 className="font-bold tracking-wide text-white/90">{t`STACKMORPH`}</h3>
                    <p className="text-xs font-medium tracking-widest text-white/50">
                      {user?.isPremium ? t`PREMIUM MEMBER` : t`FREE PLAN`}
                    </p>
                  </div>
                </div>
                {user?.isPremium && (
                  <div className="rounded-full bg-yellow-400/20 px-3 py-1 text-xs font-bold text-yellow-400 backdrop-blur-sm">
                    {t`ACTIVE`}
                  </div>
                )}
              </div>

              <div className="space-y-8">
                {user?.isPremium && (
                  <div className="space-y-2">
                    <p className="text-sm text-white/50">{t`Member since`}</p>
                    <p className="font-mono text-xl tracking-wider text-white/90">
                      {user?.premiumStartDate 
                        ? new Date(user.premiumStartDate).toLocaleDateString() 
                        : (user?.createdAt 
                          ? new Date(user.createdAt).toLocaleDateString() 
                          : "2024")}
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between border-t border-white/10 pt-6">
                  <div>
                    <p className="text-sm font-medium text-white/70">{user?.name || "User"}</p>
                    <p className="text-xs text-white/40">{user?.email}</p>
                  </div>
                  {!user?.isPremium && (
                    <Button className="rounded-full bg-white text-black hover:bg-white/90">
                      {t`Upgrade Access`}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Status/Upsell Context */}
          <div className="flex flex-col justify-center space-y-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight lg:text-4xl">
                {user?.isPremium ? t`You're a VIP Member` : t`Unlock the full experience`}
              </h1>
              <p className="mt-2 text-lg text-muted-foreground">
                {user?.isPremium 
                  ? t`Enjoy unlimited access to all features, priority support, and exclusive templates.`
                  : t`Join thousands of professionals who have accelerated their careers with StackMorph Premium.`}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: SparkleIcon, label: t`AI Assistant`, active: true },
                { icon: DiamondIcon, label: t`Premium Templates`, active: true },
                { icon: LightningIcon, label: t`Instant Downloads`, active: true },
                { icon: CheckCircleIcon, label: t`No Watermark`, active: true },
              ].map((feature, i) => (
                <div key={i} className={cn(
                  "flex items-center gap-3 rounded-xl border p-4 transition-colors",
                  user?.isPremium 
                    ? "border-primary/20 bg-primary/5 text-primary" 
                    : "border-border/50 bg-background/50 text-muted-foreground"
                )}>
                  <feature.icon size={20} weight={user?.isPremium ? "fill" : "regular"} />
                  <span className="font-medium">{feature.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 2. Main Grid: Analytics (Left) & Referral (Right) */}
        <div className="grid gap-8 lg:grid-cols-3">
          {/* Usage Analytics (2/3) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2"
          >
            <div className="mb-4 flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-full bg-blue-500/10 text-blue-500">
                <LightningIcon size={20} weight="fill" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">{t`Usage Analytics`}</h2>
                <p className="text-sm text-muted-foreground">{t`Track your resume performance`}</p>
              </div>
            </div>
            <div className="rounded-2xl border border-border/50 bg-background/50 p-6 backdrop-blur-sm">
              <UsageAnalytics />
            </div>
          </motion.div>

          {/* Referral Dashboard (1/3) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="h-full"
          >
             <ReferralDashboard />
          </motion.div>
        </div>

        {/* 3. Marketplace (Full Width) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">{t`Add-ons Marketplace`}</h2>
              <p className="text-muted-foreground">{t`Supercharge your career with premium extras`}</p>
            </div>
          </div>
          <div className="rounded-2xl border border-border/50 bg-background/50 p-6 backdrop-blur-sm">
            <AddonsMarketplace />
          </div>
        </motion.div>

        {/* 4. Feature Comparison (Bottom) */}
        {!user?.isPremium && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-2xl border border-border/50 bg-background/50 p-8 backdrop-blur-sm"
          >
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold">{t`Compare Plans`}</h2>
              <p className="text-muted-foreground">{t`Find the perfect plan for your career goals`}</p>
            </div>
            <FeatureComparison />
          </motion.div>
        )}
      </div>
    </>
  );
};
